import React, { FC, useContext, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import makeStyles from "@mui/styles/makeStyles";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { DefaultTextEditor, EmptyRawEditorState } from "components/TextEditors";
import { currentDateTimeUtc } from "core/datetime";
import styles from "assets/jss/default/components/dialogButtonStyle.js";
import FormHelperText from "@mui/material/FormHelperText";
import { createTextEditorValidationRule } from "core/utils/validation";
import { CaseEvent, CaseEventType } from "interfaces/case-event";
import {
  IFileToUpload,
  NewAttachmentsList
} from "components/Attachments/NewAttachmentList";
import { Case } from "interfaces/case";
import { useMutation } from "@tanstack/react-query";
import { AxiosError, AxiosResponse, isAxiosError } from "axios";
import { AttachmentDetail } from "interfaces/attachment";
import { AxiosContext } from "contexts/AxiosContext";
import { IApiError } from "interfaces/api-error";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import ProgressIndicatorOverlay from "components/ProgressIndicatorOverlay";

const useStyles = makeStyles(styles);

const validationSchema = yup.object({
  comment: createTextEditorValidationRule("Comment")
});

interface NewPostDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentCase: Case;
  refetchStream: () => any;
}

interface CreatePost {
  date_time: string;
  type: CaseEventType.POST;
  description: string;
  case: number;
  params: { feedComment: any[] };
  recommendation: string;
}

export const NewPostDialog: FC<NewPostDialogProps> = ({
  open,
  setOpen,
  currentCase,
  refetchStream
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const classes = useStyles();
  const textEditorRef = useRef();
  const [attachmentsToUpload, setAttachmentsToUpload] = useState<
    IFileToUpload[]
  >([]);
  const [filesDropping, setFilesDropping] = useState(false);
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const handleAxiosError = (defaultErrorMessage: string) => (
    err: AxiosError<IApiError>
  ) => {
    addSnackbarToQueue({
      severity: "error",
      message: err?.response?.data?.error || defaultErrorMessage
    });
  };

  const formik = useFormik({
    initialValues: {
      comment: EmptyRawEditorState
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const newPost: CreatePost = {
        date_time: currentDateTimeUtc(),
        type: CaseEventType.POST,
        description: JSON.stringify(values.comment),
        case: currentCase.id,
        params: {
          feedComment: []
        },
        recommendation: ""
      };

      let eventId: number;
      try {
        eventId = (await postCreateEvent(newPost)).id;
      } catch (err) {
        console.error(err);

        let message = "Error creating post.";
        if (isAxiosError(err)) {
          message = err?.response?.data?.error;
        }
        addSnackbarToQueue({
          severity: "error",
          message
        });

        return;
      }

      try {
        for (const attachment of attachmentsToUpload) {
          await usePostEventAttachment.mutateAsync({
            eventId,
            file: attachment.contents
          });
        }
      } catch (err) {
        console.error(err);
        return;
      }

      if (textEditorRef.current) {
        // @ts-ignore
        textEditorRef.current.reset();
      }

      await refetchStream();
      setOpen(false);
    }
  });

  useEffect(() => {
    return () => {
      setAttachmentsToUpload([]);
      formik.resetForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.resetForm]);

  const postCreateEvent = async (newPost: CreatePost): Promise<CaseEvent> => {
    const { data } = await axios.post<CreatePost, AxiosResponse<CaseEvent>>(
      "events",
      newPost
    );

    return data;
  };

  interface PostAttachmentVariables {
    eventId: number;
    file: File;
  }

  const usePostEventAttachment = useMutation(["post-event-attachment"], {
    mutationFn: ({ eventId, file }: PostAttachmentVariables) => {
      const formData = new FormData();
      formData.append("file", file);
      return axios
        .post<File, AxiosResponse<AttachmentDetail>>(
          `events/${eventId}/attachments`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        )
        .then(r => r);
    },
    onError: handleAxiosError("Error uploading file.")
  });

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      onClose={() => setOpen(false)}
      aria-labelledby="new-comment-dialog"
      data-qa="CP-Dialog-New-Comment"
    >
      <ProgressIndicatorOverlay loading={formik.isSubmitting} />
      <DialogTitle>New Post</DialogTitle>
      <DialogContent>
        <form id="new-comment-form" onSubmit={formik.handleSubmit}>
          <DefaultTextEditor
            // @ts-expect-error
            onChanged={formik.handleChange}
            editorKey="commentEditorInDialog"
            maxWidth="98%"
            variant="extended"
            name="comment"
            ref={textEditorRef}
            data-qa="CP-TextEdit-Post-Text"
          />
          {formik.errors?.comment ? (
            <FormHelperText error={true} variant="standard">
              <>{formik.errors.comment}</>
            </FormHelperText>
          ) : null}
        </form>
        <Divider />
        <NewAttachmentsList
          attachmentsToUpload={attachmentsToUpload}
          setAttachmentsToUpload={setAttachmentsToUpload}
          filesDropping={filesDropping}
          setFilesDropping={setFilesDropping}
        />
      </DialogContent>
      <DialogActions className={classes.buttonArea}>
        <Button
          className={classes.cancelButton}
          onClick={() => setOpen(false)}
          variant="outlined"
          color="primary"
          data-qa="CP-Button-Cancel-Post"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="new-comment-form"
          className={classes.completeButton}
          disabled={!formik.isValid || formik.isSubmitting || filesDropping}
          variant="contained"
          color="primary"
          data-qa="CP-Button-Complete-Post"
        >
          Complete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
